/**
 * Discriminated union types for paradigm-specific analysis targets.
 *
 * This replaces the generic "Behavior" interface with type-safe, paradigm-specific
 * analysis targets that enforce conceptual accuracy for each learning type.
 */

import type { FunctionalAnalysisItem, OperantAntecedentItem, RespondentChainItem, Frequency, Duration } from "@/context/functionalBehaviorAnalysisContext";

// ============================================================================
// BASE SHARED FIELDS
// ============================================================================

/** Base shared fields for all analysis targets */
interface AnalysisTargetBase {
  id: string;
  name: string;
  description: string;
  /** Learning paradigm type - discriminant field for type safety */
  learningType: 'operant' | 'respondent' | 'respondent_chain' | 'non_associative';
  /** Behavioral metrics (may be optional for some paradigms) */
  frequency?: Frequency;
  duration?: Duration;
  intensity?: number;
}

// ============================================================================
// OPERANT ANALYSIS TARGET
// ============================================================================

/**
 * Operant Analysis Target - A-B-C (Antecedent-Behavior-Consequence) paradigm
 * Focuses on goal-directed behavior maintained by consequences
 */
export interface OperantAnalysisTarget extends AnalysisTargetBase {
  learningType: 'operant';

  // Behavioral metrics (frequency/duration/intensity are behavioral measurements)
  frequency?: Frequency;
  duration?: Duration;
  intensity?: number;

  // Operant-specific analysis fields
  antecedents: OperantAntecedentItem[];
  consequences: FunctionalAnalysisItem[];
  modulatingVariables: FunctionalAnalysisItem[];

  // Paradigm-specific hypotheses
  maintenanceHypothesis: string;  // Current function maintaining the behavior
  originHypothesis?: string;      // Learning history (reinforcement/acquisition)
}

// ============================================================================
// MIXED RESPONDENT-OPERANT ANALYSIS TARGET
// ============================================================================

/**
 * Mixed Respondent-Operant Analysis Target - Hybrid paradigm
 * Operant behavior triggered by classically conditioned responses (respondent chain as antecedent)
 * Example: Anxiety (respondent) triggers avoidance behavior (operant)
 */
export interface MixedRespondentOperantTarget extends AnalysisTargetBase {
  learningType: 'respondent';
  
  // Behavioral metrics
  frequency?: Frequency;
  duration?: Duration;
  intensity?: number;
  
  // Mixed analysis fields - respondent chain triggers operant behavior
  // In the UI, the single respondent chain replaces traditional operant antecedents (SD only)
  respondentChain: RespondentChainItem;         // Single respondent chain functioning as antecedent
  antecedents?: OperantAntecedentItem[];        // Optional additional operant antecedents (SD only)
  consequences: FunctionalAnalysisItem[];       // Operant consequences maintaining the behavior
  modulatingVariables: FunctionalAnalysisItem[]; // Context factors
  
  // Paradigm-specific hypotheses
  conditioningHypothesis: string;   // How respondent chain triggers operant behavior
  acquisitionHypothesis?: string;   // How conditioning was acquired
}

// ============================================================================
// RESPONDENT ANALYSIS TARGET
// ============================================================================

/**
 * Respondent Analysis Target - Classical conditioning paradigm
 * Focuses on automatic responses to stimuli (US/UR → CS/CR)
 */
export interface RespondentAnalysisTarget extends AnalysisTargetBase {
  learningType: 'respondent_chain';

  // Respondent-specific analysis fields
  conditioningChains: RespondentChainItem[];  // US/UR/CS/CR conditioning sequences

  // Paradigm-specific hypotheses
  conditioningHypothesis: string;  // How conditioning currently maintains responses
  acquisitionHypothesis?: string;  // How conditioning was originally acquired
}

// ============================================================================
// NON-ASSOCIATIVE ANALYSIS TARGET
// ============================================================================

/**
 * Non-Associative Analysis Target - Habituation/Sensitization paradigm
 * Focuses on response changes due to repeated exposure (no associations)
 */
export interface NonAssociativeAnalysisTarget extends AnalysisTargetBase {
  learningType: 'non_associative';

  // Non-associative-specific analysis fields
  habituationFactors: FunctionalAnalysisItem[];     // Decreased responsiveness
  sensitizationFactors: FunctionalAnalysisItem[];   // Increased responsiveness

  // Paradigm-specific hypotheses
  adaptationHypothesis: string;         // How adaptation currently maintains response pattern
  exposureHistoryHypothesis?: string;   // How repeated exposure led to current state
}

// ============================================================================
// DISCRIMINATED UNION TYPE
// ============================================================================

/**
 * Discriminated union of all analysis target types.
 * TypeScript will enforce type safety based on the `learningType` discriminant.
 */
export type AnalysisTarget =
  | OperantAnalysisTarget
  | MixedRespondentOperantTarget
  | RespondentAnalysisTarget
  | NonAssociativeAnalysisTarget;

// ============================================================================
// TYPE GUARDS
// ============================================================================

/** Type guard for OperantAnalysisTarget */
export function isOperantAnalysis(target: AnalysisTarget): target is OperantAnalysisTarget {
  return target.learningType === 'operant';
}

/** Type guard for MixedRespondentOperantTarget */
export function isMixedRespondentOperant(target: AnalysisTarget): target is MixedRespondentOperantTarget {
  return target.learningType === 'respondent';
}

/** Type guard for RespondentAnalysisTarget */
export function isRespondentAnalysis(target: AnalysisTarget): target is RespondentAnalysisTarget {
  return target.learningType === 'respondent_chain';
}

/** Type guard for NonAssociativeAnalysisTarget */
export function isNonAssociativeAnalysis(target: AnalysisTarget): target is NonAssociativeAnalysisTarget {
  return target.learningType === 'non_associative';
}

// ============================================================================
// LEGACY COMPATIBILITY
// ============================================================================

/**
 * Legacy Behavior interface - kept for migration purposes
 * This will be removed once migration is complete
 */
export interface LegacyBehavior {
  id: string;
  name: string;
  description: string;
  type: string; // Old behavior type enum
  frequency: Frequency;
  duration: Duration;
  intensity: number;
  antecedents: any[]; // Mixed operant/respondent antecedents
  consequences: FunctionalAnalysisItem[];
  modulatingVariables: FunctionalAnalysisItem[];
  functionalHypothesis: string; // Old generic hypothesis field
  originHypothesis?: string;
  habituation: FunctionalAnalysisItem[];
  sensitization: FunctionalAnalysisItem[];
}

// ============================================================================
// MIGRATION HELPERS
// ============================================================================

/**
 * Migrates a legacy Behavior to the appropriate AnalysisTarget type
 */
export function migrateToAnalysisTarget(legacy: LegacyBehavior): AnalysisTarget {
  const base = {
    id: legacy.id,
    name: legacy.name,
    description: legacy.description,
  };

  const legacyAntecedents = Array.isArray(legacy.antecedents) ? legacy.antecedents : [];
  const operantAntecedents = legacyAntecedents.filter(a => a?.item_type === "operant_antecedent");
  const sdAntecedents = operantAntecedents.filter(
    a => a?.type === "Discriminative Stimulus (SD)",
  );
  const moAntecedents = operantAntecedents.filter(
    a => a?.type === "Motivating Operation (MO)",
  );
  const moModulators = moAntecedents.map(item => ({
    type: "Motivating Operation (MO)",
    description: item?.description || "",
    evidence_quote: item?.evidence_quote ?? null,
  }));
  const baseModulators = Array.isArray(legacy.modulatingVariables)
    ? legacy.modulatingVariables
    : [];
  const mergedModulators = [...baseModulators, ...moModulators];
  const respondentChains = legacyAntecedents.filter(a => a?.item_type === "respondent_chain");

  // Map legacy behavior types to new learning types
  switch (legacy.type) {
    case 'operant':
      return {
        ...base,
        learningType: 'operant',
        frequency: legacy.frequency,
        duration: legacy.duration,
        intensity: legacy.intensity,
        antecedents: sdAntecedents,
        consequences: legacy.consequences,
        modulatingVariables: mergedModulators,
        maintenanceHypothesis: legacy.functionalHypothesis,
        originHypothesis: legacy.originHypothesis,
      } as OperantAnalysisTarget;
    
    case 'respondent': // Mixed type - respondent chain + operant consequences
      const operantAnts = sdAntecedents;
      return {
        ...base,
        learningType: 'respondent',
        frequency: legacy.frequency,
        duration: legacy.duration,
        intensity: legacy.intensity,
        respondentChain: respondentChains.length > 0 ? respondentChains[0] : {
          item_type: 'respondent_chain' as const,
          description: '',
          evidence_quote: null,
          us: [],
          ur: [],
          cs: [],
          cr: [],
        },
        antecedents: operantAnts,
        consequences: legacy.consequences,
        modulatingVariables: mergedModulators,
        conditioningHypothesis: legacy.functionalHypothesis,
        acquisitionHypothesis: legacy.originHypothesis,
      } as MixedRespondentOperantTarget;

    case 'respondent_chain':
      return {
        ...base,
        learningType: 'respondent_chain',
        frequency: legacy.frequency,
        duration: legacy.duration,
        intensity: legacy.intensity,
        conditioningChains: respondentChains,
        conditioningHypothesis: legacy.functionalHypothesis,
        acquisitionHypothesis: legacy.originHypothesis,
      } as RespondentAnalysisTarget;

    case 'non_associative':
      return {
        ...base,
        learningType: 'non_associative',
        frequency: legacy.frequency,
        duration: legacy.duration,
        intensity: legacy.intensity,
        habituationFactors: legacy.habituation,
        sensitizationFactors: legacy.sensitization,
        adaptationHypothesis: legacy.functionalHypothesis,
        exposureHistoryHypothesis: legacy.originHypothesis,
      } as NonAssociativeAnalysisTarget;

    default:
      // Unknown type defaults to operant
      return {
        ...base,
        learningType: 'operant',
        frequency: legacy.frequency,
        duration: legacy.duration,
        intensity: legacy.intensity,
        antecedents: sdAntecedents,
        consequences: legacy.consequences,
        modulatingVariables: mergedModulators,
        maintenanceHypothesis: legacy.functionalHypothesis || 'Maintenance hypothesis not specified',
        originHypothesis: legacy.originHypothesis,
      } as OperantAnalysisTarget;
  }
}
